import { useState } from "react";
import { api, type Readiness, type VerifiedReadiness } from "../api.js";
import type { JSX } from "react";

interface Props {
  registerId: string;
  hasData: boolean;
}

export function ReadinessDashboard({ registerId, hasData }: Props): JSX.Element {
  const [readiness, setReadiness] = useState<Readiness | null>(null);
  const [verified, setVerified] = useState<VerifiedReadiness | null>(null);
  const [busySample, setBusySample] = useState(false);
  const [busyVerified, setBusyVerified] = useState(false);
  const [errorSample, setErrorSample] = useState<string | null>(null);
  const [errorVerified, setErrorVerified] = useState<string | null>(null);

  function runSample(): void {
    if (busySample) return;
    setBusySample(true);
    setErrorSample(null);
    api
      .validate(registerId)
      .then(setReadiness)
      .catch((err: unknown) => setErrorSample(err instanceof Error ? err.message : String(err)))
      .finally(() => setBusySample(false));
  }

  function runVerifiedValidation(): void {
    if (busyVerified) return;
    setBusyVerified(true);
    setErrorVerified(null);
    api
      .validateVerified(registerId)
      .then(setVerified)
      .catch((err: unknown) => setErrorVerified(err instanceof Error ? err.message : String(err)))
      .finally(() => setBusyVerified(false));
  }

  return (
    <>
      {!hasData && (
        <p className="muted" style={{ marginBottom: "1rem" }}>
          No data yet — import and promote arrangements via the Import tab first.
        </p>
      )}
      <div
        data-testid="readiness-panel"
        style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap" }}
      >
        {/* Sample validation panel */}
        <div className="card" style={{ flex: "1 1 340px", minWidth: "300px" }}>
          <h3>Sample Validation (DORA rules preview)</h3>
          <p className="muted">
            Runs the sample ruleset against live register rows. Not for submission.
          </p>
          <div className="actions">
            <button
              data-testid="run-validate"
              onClick={runSample}
              disabled={busySample || !hasData}
              title={!hasData ? "Import and promote data first" : undefined}
            >
              {busySample ? "Validating…" : "Run validation"}
            </button>
          </div>
          {errorSample !== null && <div className="error">{errorSample}</div>}
          {readiness !== null && (
            <div style={{ marginTop: "1rem" }}>
              <div
                className={`badge ${readiness.ready ? "badge-ready" : "badge-not-ready"}`}
                data-testid="data-ready"
                data-ready={readiness.ready ? "true" : "false"}
              >
                {readiness.ready ? "READY" : "NOT READY"}
              </div>
              <div className="totals" style={{ marginTop: "0.75rem" }}>
                <span className="badge badge-blocking">{readiness.totals.blocking} blocking</span>{" "}
                <span className="badge badge-warning">{readiness.totals.warning} warnings</span>{" "}
                <span className="badge badge-advisory">{readiness.totals.advisory} advisory</span>
              </div>
              <div className="findings-list" style={{ marginTop: "1rem" }}>
                <ul className="findings" data-testid="findings-list">
                  {readiness.findings.map((f, i) => (
                    <li key={i} className={f.severity}>
                      <span className="badge badge-{f.severity}">{f.severity}</span>{" "}
                      <span className="rule">{f.ruleId}</span>{" "}
                      <span className="msg">{f.message}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Verified validation panel */}
        <div className="card" style={{ flex: "1 1 340px", minWidth: "300px" }}>
          <h3>Verified Validation (EBA v4.0)</h3>
          <p className="muted">
            Runs primary-source-verified EBA rules. Requires template data uploaded via Import tab.
            Structural check only — <strong>not confirmation of NCA acceptance</strong>.
          </p>
          <div className="actions">
            <button
              data-testid="run-verified-validate"
              onClick={runVerifiedValidation}
              disabled={busyVerified || !hasData}
              title={!hasData ? "Import and promote data first" : undefined}
            >
              {busyVerified ? "Validating…" : "Run verified validation"}
            </button>
          </div>
          {errorVerified !== null && <div className="error">{errorVerified}</div>}
          {verified !== null && (
            <div style={{ marginTop: "1rem" }}>
              <div className="badge ok" data-testid="verified-badge">
                VERIFIED · {verified.provenance}
              </div>
              <p
                className="muted"
                style={{ marginTop: "0.25rem" }}
                data-testid="verified-disclaimer"
              >
                Structurally verified against the v4.0 taxonomy — <strong>not</strong> a submission
                or NCA acceptance decision.
              </p>
              <div className="totals" style={{ marginTop: "0.75rem" }}>
                <span className="badge badge-warning">{verified.totals.warning} warnings</span>{" "}
                <span className="badge badge-blocking">{verified.totals.blocking} blocking</span>
              </div>
              <div className="findings-list" style={{ marginTop: "1rem" }}>
                <ul className="findings">
                  {verified.findings.map((f, i) => (
                    <li key={i} className={f.severity}>
                      <span className="rule">{f.ruleId}</span>
                      <span className="loc">
                        {f.location.template}
                        {f.location.column !== null ? ` · ${f.location.column}` : ""} · row{" "}
                        {f.location.row}
                      </span>
                      <span className="msg">{f.message}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>

      {readiness !== null && (
        <div className="card" style={{ marginTop: "1.5rem" }}>
          <h3>Regulatory Signals (F12 · Article 30)</h3>
          <p className="muted" style={{ fontSize: "0.85rem", marginBottom: "0.75rem" }}>
            Concentration and CTPP advisories — not blocking, but reviewed first by supervisors.
          </p>
          {(() => {
            const f12 = readiness.findings.filter((f) => f.family === "F12");
            if (f12.length === 0) {
              return (
                <p style={{ color: "#4ade80", fontSize: "0.875rem" }}>
                  ✓ No concentration or CTPP signals detected.
                </p>
              );
            }
            return (
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {f12.map((f, i) => (
                  <li
                    key={i}
                    style={{
                      padding: "10px 0",
                      borderBottom: "1px solid var(--line)",
                      fontSize: "0.875rem",
                    }}
                  >
                    <div style={{ display: "flex", gap: "8px", alignItems: "baseline" }}>
                      <span
                        style={{
                          background: "rgba(217, 119, 6, 0.15)",
                          color: "#fbbf24",
                          border: "1px solid var(--warn)",
                          borderRadius: "4px",
                          padding: "1px 6px",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          flexShrink: 0,
                        }}
                      >
                        advisory
                      </span>
                      <span
                        style={{
                          fontFamily: "ui-monospace, monospace",
                          fontSize: "0.8rem",
                          color: "var(--muted)",
                          flexShrink: 0,
                        }}
                      >
                        {f.ruleId}
                      </span>
                    </div>
                    <p style={{ margin: "4px 0 0", color: "var(--ink)" }}>{f.message}</p>
                    {f.location.ref !== null && (
                      <p style={{ margin: "2px 0 0", fontSize: "0.78rem", color: "var(--muted)" }}>
                        {f.location.entity}
                        {f.location.ref !== null ? ` · ${f.location.ref}` : ""}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            );
          })()}
          <p style={{ fontSize: "0.75rem", color: "var(--muted)", marginTop: "0.75rem" }}>
            CTPP matching and Article 30 clause gap analysis require reference data (in progress).
          </p>
        </div>
      )}
    </>
  );
}
